import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Betrokkene, Partij } from '@app/core/interfaces/partij';
import { PartyInterface } from '@app/core/interfaces/party.interface';
import { PersonInterface } from '@app/core/interfaces/person/person.interface';
import { RoleInterface } from '@app/core/interfaces/person/role.interface';
import { InvolvedPartiesHelper } from '@app/my-zone/shared/involved-parties/involved-parties.helper';
import { NotificationInterface } from 'outshared-lib';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { PartyRolesEnum } from '@app/core';

@Component({
    selector: 'ins-involved-parties',
    templateUrl: './involved-parties.component.html',
    styleUrls: ['./involved-parties.component.scss'],
})
export class InvolvedPartiesComponent implements OnInit, OnDestroy {
    @Output() readonly loadInvolvedParties = new EventEmitter<{
        dossier_volgnummer: number,
        relatie_id: number,
    }>();
    @Input() parties$: Observable<Partij>;
    @Input() loaded: boolean;
    @Input() file_serial_number: number;
    @Input() relation_id: number;
    @Input() notifications: NotificationInterface[];

    public involvedParties$: Observable<PartyInterface[]>;
    public showEmptyResult: boolean = false;
    public open: string = null;
    public partyRoles: typeof PartyRolesEnum = PartyRolesEnum;
    private subLoaded: Subscription = new Subscription;

    ngOnInit(): void {
        this.loadInvolvedParties.emit({
            dossier_volgnummer: this.file_serial_number,
            relatie_id: this.relation_id,
        });

        this.involvedParties$ = this.parties$.pipe(map((result) => this.filterParties(result)));
    }

    ngOnDestroy(): void {
        this.subLoaded.unsubscribe();
    }

    public onOpen(code: string): void {
        this.open = code;
    }

    public onClose(): void {
        this.open = null;
    }

    private filterParties(result): PartyInterface[] {
        if (!result) {
            this.showEmptyResult = false;
            return [];
        }

        const betrokkenen = result.betrokkene;
        const natuurlijk_persoon: PersonInterface[] = (result.natuurlijk_persoon) ? result.natuurlijk_persoon : [];
        const niet_natuurlijk_persoon: PersonInterface[] = (result.niet_natuurlijk_persoon) ? result.niet_natuurlijk_persoon : [];

        const totalNP = natuurlijk_persoon.length;
        const totalNNP = niet_natuurlijk_persoon.length;

        if (totalNP < natuurlijk_persoon.length || totalNNP < niet_natuurlijk_persoon.length) {
            this.open = null;
        }

        if (this.hasInvolvedParties(betrokkenen)) {
            this.showEmptyResult = false;
        } else {
            this.showEmptyResult = !(natuurlijk_persoon.length > 0 || niet_natuurlijk_persoon.length > 0);
        }

        return this.filterInvolvedPersons(betrokkenen, natuurlijk_persoon, niet_natuurlijk_persoon);
    }

    private hasInvolvedParties(involvedParties: PartyInterface): boolean {
        let hasInvolvedParty: boolean = false;

        for (const involvedParty in involvedParties) {
            if (involvedParties.hasOwnProperty(involvedParty)) {
                const element: Betrokkene = involvedParties[involvedParty];

                if (
                    element.meerdere_toegestaan_indicatie === 'J' ||
                    element.meerdere_toegestaan_indicatie_omschrijving === 'Ja' ||
                    element.toevoegen_toegestaan_indicatie === 'J'
                ) {
                    hasInvolvedParty = true;
                }
            }
        }
        return hasInvolvedParty;
    }

    private filterInvolvedPersons(involvedParties: RoleInterface,
                                  natuurlijk_persoon: PersonInterface[],
                                  niet_natuurlijk_persoon: PersonInterface[]): PartyInterface[] {
        const array: PartyInterface[] = [];

        for (const involvedParty in involvedParties) {
            if (involvedParties.hasOwnProperty(involvedParty)) {
                const element: Betrokkene = involvedParties[involvedParty];

                // Generate partij object
                const rol: string = this.getRolCode(involvedParty);
                const partij: PartyInterface = InvolvedPartiesHelper.CreateParty({
                    personen: this.filterPersons(rol, natuurlijk_persoon, niet_natuurlijk_persoon),
                    rol: rol,
                    toevoegen_toestaan: false,
                });

                if (element.toevoegen_toegestaan_indicatie === 'J') {
                    partij.toevoegen_toestaan = !(partij.rol === this.partyRoles.CounterParty && partij.personen.length > 0);
                }

                // Add new party to array
                array.push(partij);
            }
        }
        return array;
    }

    private getRolCode(involvedPartyName: string): string {
        switch (involvedPartyName) {
            case 'bestuurder':
                return this.partyRoles.Driver;

            case 'getuigen':
            case 'getuige':
                return this.partyRoles.Witness;

            case 'contactpersoon':
                return this.partyRoles.Contact;

            case 'medeverzekerde':
                return this.partyRoles.CoInsured;

            case 'tegenpartij':
                return this.partyRoles.CounterParty;
        }
    }

    private filterPersons(rol: string, natuurlijk_persoon: PersonInterface[],
                          niet_natuurlijk_persoon: PersonInterface[]): PersonInterface[] {
        const array: PersonInterface[] = [];

        // Filter natuurlijk persoon
        for (const person in natuurlijk_persoon) {
            if (natuurlijk_persoon.hasOwnProperty(person)) {
                const element: PersonInterface = InvolvedPartiesHelper.CreatePerson(natuurlijk_persoon[person]);
                element.persoon_type = 'NP';

                if (element.rol.rol === rol) {
                    array.push(element);
                }
            }
        }

        // Filter NIET natuurlijk persoon
        for (const person in niet_natuurlijk_persoon) {
            if (niet_natuurlijk_persoon.hasOwnProperty(person)) {
                const element: PersonInterface = InvolvedPartiesHelper.CreatePerson(niet_natuurlijk_persoon[person]);
                element.persoon_type = 'NNP';

                if (element.rol.rol === rol) {
                    array.push(element);
                }
            }
        }

        return array;
    }
}
